/**
 * LeadSchedulePrivateModal — schedule a private showing for a lead.
 * Extracted from LeadDetailPage.tsx (iteration 4 refactor-split).
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '../../../../components/ui/button';
import { scheduleAppointment } from '../../../../lib/leasing.api';
import type { LeadDetail } from '../shared';

export interface LeadSchedulePrivateModalProps {
  lead: LeadDetail;
  onClose: () => void;
  onSuccess: () => void;
}

export function LeadSchedulePrivateModal({ lead, onClose, onSuccess }: LeadSchedulePrivateModalProps) {
  const [scheduledFor, setScheduledFor] = useState('');
  const [duration, setDuration] = useState('30');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Escape key + focus trap
  useEffect(() => {
    const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Focus first focusable element on open
    const dialog = dialogRef.current;
    if (dialog) {
      const firstFocusable = dialog.querySelector<HTMLElement>(FOCUSABLE);
      firstFocusable?.focus();
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const inputCls = 'w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!scheduledFor) { setError('Please select a date and time.'); return; }
    setIsSubmitting(true);
    try {
      await scheduleAppointment(lead.listingId, {
        kind: 'private',
        scheduledFor: new Date(scheduledFor).toISOString(),
        durationMinutes: duration ? parseInt(duration, 10) : undefined,
        location: location || null,
        notes: notes || null,
        leadId: lead.id,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to schedule showing');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="lead-schedule-private-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-schedule-private-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div ref={dialogRef} className="bg-card rounded-lg border border-border w-full max-w-md p-6 shadow-lg max-h-[90vh] overflow-y-auto">
        <h2 id="lead-schedule-private-title" className="text-lg font-semibold mb-4">
          Schedule Private Showing
        </h2>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4" data-testid="lead-schedule-private-form">
          <div>
            <label htmlFor="lead-appointment-date" className="block text-sm font-medium mb-1">
              Date &amp; Time <span className="text-destructive" aria-hidden="true">*</span>
            </label>
            <input
              id="lead-appointment-date"
              data-testid="lead-appointment-scheduled-for"
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
              required
              aria-required="true"
              className={inputCls}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="lead-appointment-duration" className="block text-sm font-medium mb-1">
              Duration (minutes)
            </label>
            <input
              id="lead-appointment-duration"
              data-testid="lead-appointment-duration"
              type="number"
              min="5"
              step="5"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="30"
              className={inputCls}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="lead-appointment-location" className="block text-sm font-medium mb-1">
              Location <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              id="lead-appointment-location"
              data-testid="lead-appointment-location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Front door"
              className={inputCls}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="lead-appointment-notes" className="block text-sm font-medium mb-1">
              Notes <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <textarea
              id="lead-appointment-notes"
              data-testid="lead-appointment-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring resize-y"
              disabled={isSubmitting}
            />
          </div>

          {error && (
            <div
              data-testid="lead-schedule-private-error"
              className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              data-testid="lead-schedule-private-submit"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Scheduling…' : 'Schedule'}
            </Button>
            <Button
              type="button"
              variant="outline"
              data-testid="lead-schedule-private-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
